import { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, getDocs, deleteDoc, doc, getDoc, writeBatch, updateDoc } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import { db } from '../../../config/firebaseConfig';
import { simpleBreakdown } from '../salaryMath';
import useSalaryPaymentText from './salaryPaymentText';
import { MONEY_KEYS, getCurrentYearMonth } from './salaryPaymentColumns';

/**
 * The amounts the old table showed for a salary: the stored total
 * deductions / net / cost when present, otherwise the fixed-rate figures
 * (cost fallback ×1.175, as the old table).
 */
export const salaryView = (salary) => {
  const gross = Number(salary.grossSalary) || 0;
  const rates = simpleBreakdown(gross);
  return {
    grossSalary: gross,
    sskEmployeeShare: rates.sskEmployeeShare,
    unemploymentEmployeeShare: rates.unemploymentEmployeeShare,
    stampTax: rates.stampTax,
    totalDeductions: Number(salary.totalDeductions) || rates.totalDeductions,
    netSalary: Number(salary.netSalary) || rates.netSalary,
    sskEmployerShare: rates.sskEmployerShare,
    unemploymentEmployerShare: rates.unemploymentEmployerShare,
    suggestedCost: Number(salary.suggestedCost) || gross * 1.175,
  };
};

/** One payment of the bulk preview (fixed rates, editable extras). */
const toPreview = (salary) => {
  // Ensure grossSalary is a number
  const values = simpleBreakdown(parseFloat(salary.grossSalary) || 0);
  return {
    id: salary.id,
    employeeName: salary.employeeName,
    employeeSurname: salary.employeeSurname,
    grossSalary: values.grossSalary,
    netSalary: values.netSalary,
    totalDeductions: values.totalDeductions,
    totalEmployerCost: values.totalEmployerCost,
    sskEmployeeShare: values.sskEmployeeShare,
    unemploymentEmployeeShare: values.unemploymentEmployeeShare,
    stampTax: values.stampTax,
    sskEmployerShare: values.sskEmployerShare,
    unemploymentEmployerShare: values.unemploymentEmployerShare,
    additionalPayment: 0, // Ek ödeme
    additionalDeduction: 0, // Ek kesinti
  };
};

const lower = (value) => String(value ?? '').toLocaleLowerCase('tr-TR');

/**
 * Data of Maaş Ödemeleri: every employee's latest salary record with the
 * chosen month's payment, paying one or everyone (with extra payments /
 * deductions and the travel allowance), taking a payment back, deleting a
 * record, the filters and the Excel export. Firestore writes are the old
 * page's (payments.<YYYY-MM> on the Salaries record).
 */
const useSalaryPayments = () => {
  const { label, formatMoney } = useSalaryPaymentText();
  const [salaries, setSalaries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(getCurrentYearMonth());
  const [filters, setFilters] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedId, setSelectedId] = useState(null);

  const fetchSalaries = useCallback(async () => {
    try {
      const [querySnapshot, employeesSnapshot] = await Promise.all([
        getDocs(collection(db, 'Salaries')),
        getDocs(collection(db, 'Employees')),
      ]);
      const employeesById = new Map(employeesSnapshot.docs.map((docSnapshot) => [docSnapshot.id, docSnapshot.data()]));

      // Group salaries by employee and keep only the latest one
      const salariesByEmployee = {};
      querySnapshot.docs.forEach((docSnapshot) => {
        const salaryDoc = { id: docSnapshot.id, ...docSnapshot.data() };
        const { employeeId } = salaryDoc;
        if (!salariesByEmployee[employeeId]) {
          salariesByEmployee[employeeId] = salaryDoc;
        } else {
          // Compare timestamps (updatedAt or createdAt) and keep the latest
          const currentTime = salariesByEmployee[employeeId].updatedAt || salariesByEmployee[employeeId].createdAt || 0;
          const newTime = salaryDoc.updatedAt || salaryDoc.createdAt || 0;
          if (newTime > currentTime) salariesByEmployee[employeeId] = salaryDoc;
        }
      });

      const salaryData = Object.values(salariesByEmployee).map((salaryDoc) => {
        const employeeData = employeesById.get(salaryDoc.employeeId) || {};
        const isSelectedMonthPaid = !!salaryDoc.payments?.[selectedMonth];
        return {
          ...salaryDoc,
          employeeName: employeeData.eName || '',
          employeeSurname: employeeData.eSurname || '',
          isCurrentMonthPaid: isSelectedMonthPaid,
          currentMonth: selectedMonth,
          lastPaymentDate: salaryDoc.payments?.[selectedMonth]?.paidDate || null,
        };
      });

      setSalaries(salaryData);
    } catch (error) {
      console.error('Error fetching salaries:', error);
      toast.error(label('toast.loadError'));
    } finally {
      setIsLoading(false);
    }
  }, [selectedMonth, label]);

  useEffect(() => {
    setIsLoading(true);
    fetchSalaries();
  }, [fetchSalaries]);

  const rows = useMemo(() => salaries
    .filter((salary) => !salary.isEmpty)
    .map((salary) => ({ ...salary, view: salaryView(salary) }))
    .sort((a, b) => `${a.employeeName} ${a.employeeSurname}`.localeCompare(`${b.employeeName} ${b.employeeSurname}`, 'tr')), [salaries]);

  const filteredRows = useMemo(() => {
    let filtered = rows;

    // Search: name and surname
    if (searchTerm) {
      const lowercasedSearch = lower(searchTerm);
      filtered = filtered.filter((salary) => lower(salary.employeeName).includes(lowercasedSearch) || lower(salary.employeeSurname).includes(lowercasedSearch));
    }

    // Column filters (amounts: the shown figure or the plain number)
    Object.keys(filters).forEach((key) => {
      const value = filters[key];
      if (!value) return;
      if (key === 'isCurrentMonthPaid') {
        filtered = filtered.filter((salary) => String(salary.isCurrentMonthPaid) === value);
      } else if (MONEY_KEYS.includes(key)) {
        const needle = lower(value);
        filtered = filtered.filter((salary) => {
          const amount = salary.view[key];
          return lower(formatMoney(amount)).includes(needle) || String(Math.round(amount * 100) / 100).includes(needle);
        });
      } else {
        filtered = filtered.filter((salary) => lower(salary[key]).includes(lower(value)));
      }
    });

    return filtered;
  }, [rows, filters, searchTerm, formatMoney]);

  const stats = useMemo(() => {
    const unpaid = rows.filter((salary) => !salary.isCurrentMonthPaid);
    return {
      total: rows.length,
      paid: rows.length - unpaid.length,
      unpaid: unpaid.length,
      // Toplu ödemede ek ödeme / kesinti olmadan ödenecek net toplam
      toPay: unpaid.reduce((sum, salary) => sum + toPreview(salary).netSalary, 0),
    };
  }, [rows]);

  const selectedSalary = useMemo(() => rows.find((salary) => salary.id === selectedId) || null, [rows, selectedId]);

  const handleFilter = useCallback((column, value) => setFilters((current) => ({ ...current, [column]: value })), []);
  const clearFilters = useCallback(() => {
    setFilters({});
    setSearchTerm('');
  }, []);

  /** Unpaid salaries of the month for the bulk payment preview, or null. */
  const buildPaymentPreview = () => {
    const unpaidSalaries = salaries.filter((salary) => !salary.isCurrentMonthPaid);
    if (unpaidSalaries.length === 0) {
      toast.info(label('toast.nothingToPay'));
      return null;
    }
    return unpaidSalaries.map(toPreview);
  };

  const handleBulkPayment = async (paymentPreviewData) => {
    const batch = writeBatch(db);
    let updatedCount = 0;
    try {
      paymentPreviewData.forEach((paymentData) => {
        const salaryRef = doc(db, 'Salaries', paymentData.id);
        batch.update(salaryRef, {
          [`payments.${selectedMonth}`]: {
            paidDate: new Date().toISOString(),
            amount: paymentData.netSalary,
            grossAmount: paymentData.grossSalary,
            totalDeductions: paymentData.totalDeductions,
            suggestedCost: paymentData.totalEmployerCost,
            sskEmployeeShare: paymentData.sskEmployeeShare,
            unemploymentEmployeeShare: paymentData.unemploymentEmployeeShare,
            stampTax: paymentData.stampTax,
            sskEmployerShare: paymentData.sskEmployerShare,
            unemploymentEmployerShare: paymentData.unemploymentEmployerShare,
            additionalPayment: paymentData.additionalPayment || 0,
            additionalDeduction: paymentData.additionalDeduction || 0,
          },
        });
        updatedCount++;
      });

      await batch.commit();
      toast.success(label('toast.bulkPaid', { count: updatedCount }));
      fetchSalaries();
      return true;
    } catch (error) {
      console.error('Toplu ödeme sırasında hata:', error);
      toast.error(label('toast.payError'));
      return false;
    }
  };

  const handleSinglePayment = async (salary) => {
    const name = `${salary.employeeName} ${salary.employeeSurname}`;
    if (salary.isCurrentMonthPaid) {
      toast.info(label('toast.alreadyPaid', { name }));
      return;
    }
    try {
      const salaryRef = doc(db, 'Salaries', salary.id);
      const values = simpleBreakdown(salary.grossSalary);
      await updateDoc(salaryRef, {
        [`payments.${selectedMonth}`]: {
          paidDate: new Date().toISOString(),
          amount: values.netSalary,
          grossAmount: salary.grossSalary,
          totalDeductions: values.totalDeductions,
          suggestedCost: values.totalEmployerCost,
          sskEmployeeShare: values.sskEmployeeShare,
          unemploymentEmployeeShare: values.unemploymentEmployeeShare,
          stampTax: values.stampTax,
          sskEmployerShare: values.sskEmployerShare,
          unemploymentEmployerShare: values.unemploymentEmployerShare,
        },
      });
      toast.success(label('toast.paid', { name }));
      fetchSalaries();
    } catch (error) {
      console.error('Ödeme sırasında hata:', error);
      toast.error(label('toast.payError'));
    }
  };

  /** Removes the month's payment (asked first by the page). */
  const handleReversePayment = async (salary) => {
    const name = `${salary.employeeName} ${salary.employeeSurname}`;
    if (!salary.isCurrentMonthPaid) {
      toast.info(label('toast.notPaid', { name }));
      return false;
    }
    try {
      const salaryRef = doc(db, 'Salaries', salary.id);
      // Keep the other months' payments
      const salaryDoc = await getDoc(salaryRef);
      const currentData = salaryDoc.data();
      const payments = { ...currentData.payments };
      delete payments[selectedMonth];
      await updateDoc(salaryRef, { payments });
      toast.success(label('toast.reversed', { name }));
      fetchSalaries();
      return true;
    } catch (error) {
      console.error('Ödeme geri alma sırasında hata:', error);
      toast.error(label('toast.reverseError'));
      return false;
    }
  };

  /** Deletes the salary record (asked first by the page). */
  const handleDeleteSalary = async (salary) => {
    if (!salary) return false;
    try {
      await deleteDoc(doc(db, 'Salaries', salary.id));
      toast.success(label('toast.deleted'));
      setSelectedId(null);
      fetchSalaries();
      return true;
    } catch (error) {
      console.error('Error deleting salary:', error);
      toast.error(label('toast.deleteError'));
      return false;
    }
  };

  const downloadExcel = () => {
    const dataToExport = salaries.map((salary) => ({
      'Sözleşme Tarihi': salary.contractDate,
      'İsim': salary.employeeName,
      'Soyisim': salary.employeeSurname,
      'Brüt Maaş': salary.grossSalary,
      'Gelir Vergisi': salary.incomeTax || (salary.grossSalary * 0.15), // Default calculation
      'Sigorta Primi': salary.insurancePremium || (salary.grossSalary * 0.14 + salary.grossSalary * 0.01),
      'Önerilen Maliyet': salary.suggestedCost || (salary.grossSalary * 1.37), // Default calculation (gross + employer shares)
      'Toplam Net': salary.totalNet || (salary.grossSalary - (salary.grossSalary * 0.15 + salary.grossSalary * 0.14 + salary.grossSalary * 0.01)),
      'Toplam Maaş': salary.totalSalary || salary.grossSalary,
      'Ödeme Durumu': salary.isCurrentMonthPaid ? 'Ödendi' : 'Ödenmedi',
      'Son Ödeme Tarihi': salary.lastPaymentDate ? new Date(salary.lastPaymentDate).toLocaleDateString('tr-TR') : '-',
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Salaries');
    XLSX.writeFile(workbook, 'MaasListesi.xlsx');
  };

  return {
    salaries,
    rows,
    isLoading,
    filteredRows,
    stats,
    selectedMonth,
    setSelectedMonth,
    filters,
    handleFilter,
    clearFilters,
    searchTerm,
    setSearchTerm,
    selectedSalary,
    setSelectedId,
    buildPaymentPreview,
    handleBulkPayment,
    handleSinglePayment,
    handleReversePayment,
    handleDeleteSalary,
    downloadExcel,
    refresh: fetchSalaries,
  };
};

export default useSalaryPayments;
